// Contacts (LinkedIn outreach) panel — runs the `contacto` command for a
// session. Extracted from the old inline version in Interview.tsx.
// localStorage key: nice2job.job.contacto-{sessionId} (kept stable).

import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, Copy } from 'lucide-react';
import Progress from '../ui/Progress';
import { useJobRunner } from '../../lib/useJobRunner';
import { cleanLLMOutput } from '../../lib/clean-llm-output';
import type { Application } from '../../lib/types';

export default function ContactsPanel({
  session,
  sessionId,
}: {
  session: Application;
  sessionId: string;
}) {
  const [contactName, setContactName] = useState('');
  const [contactRole, setContactRole] = useState('Hiring manager');
  const [customNote, setCustomNote] = useState('');
  // Reattach: contacto payload has company + jobRole. Match by company
  // (case-insensitive) — a user rarely has two simultaneous contacto
  // runs for the same company, so this is precise enough.
  const sessionCompanyLower = (session.company || '').toLowerCase();
  const runner = useJobRunner(`contacto-${sessionId}`, {
    reattach: {
      cmd: 'contacto',
      match: (p) => !!sessionCompanyLower && String(p.company || '').toLowerCase() === sessionCompanyLower,
    },
  });
  const cleanLog = useMemo(() => cleanLLMOutput(runner.log || ''), [runner.log]);

  const start = () => {
    if (runner.isRunning) return;
    runner.startJob('contacto', {
      contactName,
      contactRole,
      company: session.company,
      jobRole: session.role,
      customNote,
    });
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 12, lineHeight: 1.55 }}>
        Draft a LinkedIn connection request + follow-up message. We classify the contact type
        (Recruiter / Hiring Manager / Peer) and pick the right framework + your strongest
        proof point from the CV.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <div className="tab" style={{ marginBottom: 4 }}>Contact name</div>
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="e.g. Ana García"
            style={inputStyle}
          />
        </div>
        <div>
          <div className="tab" style={{ marginBottom: 4 }}>Contact role</div>
          <input
            value={contactRole}
            onChange={(e) => setContactRole(e.target.value)}
            placeholder="e.g. Engineering Manager"
            style={inputStyle}
          />
        </div>
      </div>

      <div className="tab" style={{ marginBottom: 4 }}>Optional context</div>
      <textarea
        value={customNote}
        onChange={(e) => setCustomNote(e.target.value)}
        rows={2}
        placeholder="Anything to weave in — they wrote an article, you were referred by X, etc."
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 1 }} />
        <button
          className="btn accent"
          onClick={start}
          disabled={runner.isRunning || !contactName.trim()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title={!contactName.trim() ? 'Enter a contact name first' : 'Draft connection request + follow-up'}
        >
          <Sparkles size={13} /> {runner.isRunning ? 'Drafting…' : cleanLog ? 'Re-draft' : 'Draft outreach'}
        </button>
      </div>

      {runner.isActive && (
        <div style={{ marginTop: 12 }}>
          <Progress value={Math.min(95, runner.log.length / 30)} />
          <div style={{ fontSize: 10.5, color: 'var(--ink3)', marginTop: 4 }}>
            {runner.status} · {runner.log.length.toLocaleString()} chars
            {runner.status === 'done' && (
              <>
                {' · '}
                <button
                  className="btn ghost"
                  onClick={() => navigator.clipboard?.writeText(cleanLog)}
                  style={{ fontSize: 10.5, padding: '1px 6px' }}
                >
                  <Copy size={10} /> Copy all
                </button>
                {' · '}
                <button className="btn ghost" onClick={runner.dismiss} style={{ fontSize: 10.5, padding: '1px 6px' }}>
                  Clear
                </button>
              </>
            )}
            {runner.isRunning && (
              <>
                {' · '}
                <button className="btn ghost" onClick={runner.cancel} style={{ fontSize: 10.5, padding: '1px 6px', color: '#b43a3a' }}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {runner.errorLine && (
        <div style={{ padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)', marginTop: 10 }}>
          {runner.errorLine}
        </div>
      )}

      {cleanLog && (
        <div className="report-md" style={{ marginTop: 12, fontSize: 13, lineHeight: 1.65, color: 'var(--ink)', padding: '12px 14px', background: 'rgba(0,0,0,0.02)', border: '1px solid var(--line2)', borderRadius: 6 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLog}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--line)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'Inter, var(--cjk-sans), sans-serif',
  background: 'var(--bg)',
};
